import React, { useEffect, useMemo, useState } from "react";
import { adminApi, crudApi } from "../services/api";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  phone: "",
  state: "",
  city: "",
  sub_registers: [],
  profile_image: "",
  isActive: true,
};

export default function AdminPartnerManager() {
  const [partners, setPartners] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [subRegisters, setSubRegisters] = useState([]);
  const [cars, setCars] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [carAssignId, setCarAssignId] = useState("");
  const [carSubRegister, setCarSubRegister] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const filteredCities = useMemo(
    () => cities.filter((c) => String(c.state?._id || c.state) === String(form.state)),
    [cities, form.state]
  );

  const filteredSubs = useMemo(
    () => subRegisters.filter((s) => String(s.city?._id || s.city) === String(form.city)),
    [subRegisters, form.city]
  );

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [partnerRes, stateRes, cityRes, subRes, carRes] = await Promise.all([
        adminApi.partners.list(),
        crudApi("states").list(),
        crudApi("cities").list(),
        crudApi("sub-registers").list(),
        crudApi("cars").list(),
      ]);
      setPartners(partnerRes.data || []);
      setStates(stateRes.data || []);
      setCities(cityRes.data || []);
      setSubRegisters(subRes.data || []);
      setCars(carRes.data || []);
    } catch (err) {
      setError(err.message || "Failed to load partner data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const editPartner = (partner) => {
    setEditingId(partner._id);
    setForm({
      name: partner.name || "",
      email: partner.email || partner.user?.email || "",
      password: "",
      phone: partner.ph_num || partner.user?.phone || "",
      state: partner.state?._id || partner.state || "",
      city: partner.city?._id || partner.city || "",
      sub_registers: (partner.sub_registers || []).map((s) => s._id || s),
      profile_image: partner.profile_image || "",
      isActive: partner.isActive !== false,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        sub_register: form.sub_registers[0] || "",
      };
      if (editingId) {
        await adminApi.partners.update(editingId, payload);
      } else {
        await adminApi.partners.create(payload);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removePartner = async (id) => {
    if (!window.confirm("Delete this partner and their login account?")) return;
    try {
      await adminApi.partners.remove(id);
      if (selectedPartnerId === id) setSelectedPartnerId(null);
      await load();
    } catch (err) {
      setError(err.message || "Delete failed");
    }
  };

  const viewPartnerCars = async (id) => {
    setSelectedPartnerId(id);
    try {
      const res = await adminApi.partners.cars(id);
      setCars(res.data || []);
    } catch (err) {
      setError(err.message || "Failed to load partner cars");
    }
  };

  const assignCar = async (e) => {
    e.preventDefault();
    if (!carAssignId || !carSubRegister) return;
    try {
      await adminApi.partners.assignCarSubRegister(carAssignId, carSubRegister);
      if (selectedPartnerId) await viewPartnerCars(selectedPartnerId);
      setCarAssignId("");
      setCarSubRegister("");
    } catch (err) {
      setError(err.message || "Assignment failed");
    }
  };

  if (loading) return <div className="admin-loading">Loading partners…</div>;

  return (
    <div>
      <h2>Partner Management</h2>
      <p className="admin-muted">
        Partners are assigned to sub registers. Cars inherit partner coverage through their sub
        register.
      </p>
      {error ? <div className="admin-error">{error}</div> : null}

      <form className="admin-form-grid" onSubmit={submit}>
        <h3>{editingId ? "Edit Partner" : "Create Partner"}</h3>
        <label>
          Name
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </label>
        <label>
          Email
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </label>
        <label>
          Password {editingId ? "(leave blank to keep)" : ""}
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required={!editingId}
          />
        </label>
        <label>
          Mobile Number
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
        </label>
        <label>
          State
          <select
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value, city: "", sub_registers: [] })}
            required
          >
            <option value="">Select state</option>
            {states.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          City
          <select
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value, sub_registers: [] })}
            required
            disabled={!form.state}
          >
            <option value="">Select city</option>
            {filteredCities.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sub Register
          <select
            multiple
            value={form.sub_registers}
            onChange={(e) =>
              setForm({
                ...form,
                sub_registers: Array.from(e.target.selectedOptions, (o) => o.value),
              })
            }
            required
            disabled={!form.city}
          >
            {filteredSubs.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Profile Image URL
          <input
            value={form.profile_image}
            onChange={(e) => setForm({ ...form, profile_image: e.target.value })}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />{" "}
          Active
        </label>
        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
            {saving ? "Saving…" : editingId ? "Update Partner" : "Create Partner"}
          </button>
          {editingId ? (
            <button type="button" className="admin-btn admin-btn-ghost" onClick={resetForm}>
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      <div className="admin-table-wrap" style={{ marginTop: 32 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Sub Registers</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner) => (
              <tr key={partner._id}>
                <td>{partner.name}</td>
                <td>{partner.email || partner.user?.email}</td>
                <td>{partner.ph_num}</td>
                <td>{(partner.sub_registers || []).map((s) => s.name).join(", ")}</td>
                <td>{partner.isActive === false ? "Inactive" : "Active"}</td>
                <td>
                  <button type="button" className="admin-btn admin-btn-ghost" onClick={() => editPartner(partner)}>
                    Edit
                  </button>{" "}
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => viewPartnerCars(partner._id)}
                  >
                    Cars
                  </button>{" "}
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => removePartner(partner._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form className="admin-form-grid" style={{ marginTop: 32 }} onSubmit={assignCar}>
        <h3>Assign Car to Sub Register</h3>
        <label>
          Car
          <select value={carAssignId} onChange={(e) => setCarAssignId(e.target.value)} required>
            <option value="">Select car</option>
            {cars.map((car) => (
              <option key={car._id} value={car._id}>
                {car.year} {car.brand?.name} {car.model?.name} — {car.sub_register?.name || "No sub"}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sub Register
          <select
            value={carSubRegister}
            onChange={(e) => setCarSubRegister(e.target.value)}
            required
          >
            <option value="">Select sub register</option>
            {subRegisters.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="admin-btn admin-btn-primary">
          Assign Car
        </button>
      </form>

      {selectedPartnerId ? (
        <div className="admin-card" style={{ marginTop: 24 }}>
          <h3>Assigned Cars</h3>
          <ul>
            {cars.map((car) => (
              <li key={car._id}>
                {car.year} {car.brand?.name} {car.model?.name} — {car.sub_register?.name} — {car.status}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
